import { useState, useCallback } from 'react'
import axios from 'lib/axios'
import { SnowflakeThread, SnowflakeThreadDetailResponse, UseThreadsOptions, UseThreadsReturn } from '../utils/types'
import { SYSTEM_THREAD_PREFIX } from './useThreadTitleGenerator'

// Proxy endpoint for thread management (proxy handles auth automatically)
const THREADS_ENDPOINT = '/api/v4/cortex/threads'

/**
 * Extract base URL from agent endpoint
 * @deprecated No longer needed - using proxy endpoints
 */
export function getBaseUrlFromEndpoint(agentEndpoint: string): string {
  try {
    const url = new URL(agentEndpoint)
    return url.origin
  } catch {
    return ''
  }
}

/**
 * Custom hook for managing Snowflake Cortex Agent threads
 *
 * @example
 * ```tsx
 * const { threads, loadThreads, createThread } = useThreads({
 *   originApplication: 'onyxia'
 * })
 * ```
 */
export function useThreads(options: UseThreadsOptions): UseThreadsReturn {
  const { originApplication = 'onyxia' } = options

  const [threads, setThreads] = useState<SnowflakeThread[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Create a thread without adding it to the visible list.
   * Used for creating threads on first message - thread will appear after title generation.
   */
  const createThreadSilent = useCallback(
    async (name?: string): Promise<string> => {
      const body: Record<string, string> = {}
      if (originApplication) {
        body.origin_application = originApplication
      }
      if (name) {
        body.name = name
      }

      const response = await axios.post(THREADS_ENDPOINT, body)
      const data = response.data
      const threadId = String(data.thread_id)

      // Don't add to local state - thread will appear after loadThreads is called
      return threadId
    },
    [originApplication]
  )

  /**
   * Fetch all threads for the current user
   */
  const loadThreads = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Filter threads by origin_application (user ID) if provided
      const params = originApplication ? { origin_application: originApplication } : {}

      const response = await axios.get(THREADS_ENDPOINT, { params })

      const data = response.data
      // API returns array directly, not { threads: [...] }
      const threadsArray = Array.isArray(data) ? data : data.threads || []
      // Normalize thread_id to string and sort by created_on descending (newest first)
      const normalizedThreads = threadsArray.map((t: SnowflakeThread) => ({
        ...t,
        thread_id: String(t.thread_id),
        // Map thread_name to name for consistency
        name: t.name || t.thread_name || ''
      }))
      // Filter out system threads (used for title generation) - safety measure
      // System threads have origin_application starting with SYSTEM_THREAD_PREFIX
      const filteredThreads = normalizedThreads.filter(
        (t: SnowflakeThread) => !t.origin_application?.startsWith(SYSTEM_THREAD_PREFIX)
      )
      const sortedThreads = filteredThreads.sort(
        (a: SnowflakeThread, b: SnowflakeThread) => b.created_on - a.created_on
      )
      setThreads(sortedThreads)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load threads')
      setError(error)
      console.error('Error loading threads:', error)
    } finally {
      setIsLoading(false)
    }
  }, [originApplication])

  /**
   * Create a new thread
   * @returns The new thread ID
   */
  const createThread = useCallback(
    async (name?: string): Promise<string> => {
      const body: Record<string, string> = {}
      if (originApplication) {
        body.origin_application = originApplication
      }
      if (name) {
        body.name = name
      }

      const response = await axios.post(THREADS_ENDPOINT, body)

      const data = response.data
      const threadId = String(data.thread_id)

      // Add to local state
      const newThread: SnowflakeThread = {
        thread_id: threadId,
        created_on: Date.now(),
        origin_application: originApplication,
        name: name
      }
      setThreads((prev) => [newThread, ...prev])
      setActiveThreadId(threadId)

      return threadId
    },
    [originApplication]
  )

  /**
   * Load a specific thread with its messages
   * @param threadId - Thread ID to load
   * @param options - Optional pagination parameters
   */
  const loadThread = useCallback(
    async (
      threadId: string,
      options?: { pageSize?: number; offset?: number }
    ): Promise<SnowflakeThreadDetailResponse> => {
      const pageSize = options?.pageSize ?? 100
      const offset = options?.offset ?? 0

      const response = await axios.get(`${THREADS_ENDPOINT}/${threadId}`, {
        params: { page_size: pageSize, offset }
      })

      const data = response.data

      // Only set active thread on initial load (offset 0)
      if (offset === 0) {
        setActiveThreadId(threadId)
      }

      // Determine if there are more messages
      // If API returns exactly page_size messages, there might be more
      const messages = data.messages || []
      const hasMore = messages.length === pageSize

      return {
        ...data,
        has_more: hasMore
      } as SnowflakeThreadDetailResponse
    },
    []
  )

  /**
   * Delete a thread
   */
  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      await axios.delete(`${THREADS_ENDPOINT}/${threadId}`)

      // Remove from local state
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId))

      // Clear active thread if it was deleted
      if (activeThreadId === threadId) {
        setActiveThreadId(null)
      }
    },
    [activeThreadId]
  )

  /**
   * Rename a thread
   */
  const renameThread = useCallback(async (threadId: string, name: string): Promise<void> => {
    // Optimistically update local state immediately for instant UI feedback
    setThreads((prev) => prev.map((t) => (t.thread_id === threadId ? { ...t, name } : t)))

    // Snowflake API uses 'thread_name' field
    await axios.post(`${THREADS_ENDPOINT}/${threadId}`, { thread_name: name })
  }, [])

  return {
    threads,
    activeThreadId,
    isLoading,
    error,
    loadThreads,
    createThread,
    createThreadSilent,
    loadThread,
    deleteThread,
    renameThread,
    setActiveThreadId
  }
}
